import { EditOutlined } from "@ant-design/icons"
import { Button, DatePicker, Flex, Popover, Typography } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../../../hooks/use-date-formatter.tsx"
import type { LeadHeaderPatch } from "./lead-header-patch.ts"

const { Text } = Typography

interface LeadReceivedDateProps {
  receivedOn?: string | null
  editable?: boolean
  onPatch: (patch: LeadHeaderPatch) => Promise<boolean>
}

/** Sets the day an imported or late-entered lead really came in; when set it
 *  is the lead's Created On and the day the Leads Added tile counts it. */
export const LeadReceivedDate = ({
  receivedOn,
  editable,
  onPatch
}: LeadReceivedDateProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { timezone, dateFormat } = useDateFormatter()

  if (!editable) return null

  const save = async (value: string | null) => {
    if (await onPatch({ received_on: value })) setOpen(false)
  }

  const content = (
    <Flex vertical gap={8} style={{ maxWidth: 260 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t(
          "leadWorkspace.header.receivedHelp",
          "The day the lead came in, if it was entered later (for example an imported lead)."
        )}
      </Text>
      <DatePicker
        autoFocus
        format={dateFormat}
        placeholder={dateFormat}
        value={receivedOn ? dayjs(receivedOn) : null}
        // The server refuses a day after the account's today, not the browser's.
        disabledDate={(day: Dayjs) =>
          day.format("YYYY-MM-DD") > dayjs().tz(timezone).format("YYYY-MM-DD")
        }
        onChange={(day) => day && void save(day.format("YYYY-MM-DD"))}
        allowClear={false}
      />
      {receivedOn && (
        <Button size="small" onClick={() => void save(null)}>
          {t("leadWorkspace.header.useEntryDate", "Use entry date")}
        </Button>
      )}
    </Flex>
  )

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      title={t("leadWorkspace.header.receivedTitle", "Lead received date")}
      content={content}
      destroyOnHidden
    >
      <Button
        type="text"
        size="small"
        icon={<EditOutlined />}
        aria-label={t(
          "leadWorkspace.header.editReceived",
          "Edit received date"
        )}
      />
    </Popover>
  )
}
